// The single bridge between the simulation worker and React. The worker posts a snapshot ~10 times a
// second; this store holds the latest one plus the append-only event log, the player's selection, and
// the targeting mode the order deck is in. React components subscribe with narrow selectors; the map
// overlay subscribes imperatively (see LiveStage) so it never re-renders the tree.
//
// It also carries the pre-fight state: the chosen AO (area of operations), the fitted scenario with any
// control graphics the player drew during planning, and the plan (one order card per tasked unit) that
// is issued as a single play the moment the fight starts.

import { create } from 'zustand';
import { Grid } from '../sim/grid';
import { SimClient } from '../sim/client';
import { fitScenario } from '../sim/scenario';
import { isLegal, type Legality } from '../sim/deck';
import { DEFAULT_DIALS } from '../sim/types';
import type { AoMeta, CardId, Dials, Event, EventKind, Graphic, Scenario, Snapshot, Target, Unit } from '../sim/types';

export type Stage = 'scenario' | 'planning' | 'live' | 'debrief' | 'design';

/**
 * Ticks per real second: `0` pauses. Every other value is a sim-seconds-per-real-second ratio (pitch 2,
 * "time you can feel") - `ticksPerSec * TICK_SEC` sim-seconds pass per real second, so the four named
 * speeds below read as `1:1`, `10:1`, `60:1` and `240:1` on the chips. A float rather than an enum of
 * integers because 1:1 and 10:1 both land on a fraction of a tick per second (one tick is six sim
 * seconds): the worker's scheduler accumulates the fraction across wakes (src/sim/worker.ts `wake`).
 */
export type Speed = number;

/** 1 sim-second per real second: one tick (six sim seconds) every six real seconds. */
export const SPEED_1_1: Speed = 1 / 6;
/** 10 sim-seconds per real second. */
export const SPEED_10_1: Speed = 10 / 6;
/** 60 sim-seconds per real second - the default, and the old "1x". */
export const SPEED_60_1: Speed = 10;
/** 240 sim-seconds per real second - the old "4x". */
export const SPEED_240_1: Speed = 40;

/** How long, in real milliseconds, an auto-slow (pitch 2) stays quiet after it fires once. */
export const AUTO_SLOW_COOLDOWN_MS = 30_000;

/**
 * How long the clock stays at 10:1 after an auto-slow before putting itself back, if the player does
 * nothing. Long enough to read the report that caused it and watch the next few sim minutes land;
 * short enough that a 240:1 run does not turn into a 10:1 run because a contact came in while the
 * player was looking at the map.
 */
export const AUTO_SLOW_RESTORE_MS = 20_000;

/**
 * An auto-slow in progress (pitch 2, "time you can feel").
 *
 * The clock dropped itself to 10:1 for a significant event; `previousSpeed` is where it came from and
 * where it goes back to. It goes back by itself at `restoreAt` - unless the player did something in
 * the meantime (issued an order, selected a unit, pressed a speed chip), which sets `held`: someone
 * who is acting on the event is not waiting for the clock to run away from them again, so the
 * restore is cancelled and the resume chip becomes theirs to press.
 */
export interface AutoSlow {
  previousSpeed: Speed;
  /** `Date.now()` at which the clock returns to `previousSpeed` on its own. */
  restoreAt: number;
  /** The player interacted during the window: hold 10:1 and wait to be resumed by hand. */
  held: boolean;
}

export interface AoBuffers { cost: ArrayBuffer; cover: ArrayBuffer; elev: ArrayBuffer; roads: ArrayBuffer }

/** One tasked unit in the plan: the card it will be given at H-hour and what it is aimed at. */
export interface PlanOrder { unitId: number; card: CardId; target: Target }

/**
 * A line the staff puts in the report feed that did not come from the simulation.
 *
 * There is exactly one so far: the planning clock running out. That is a thing that happened to the
 * player's plan, so it belongs on the net with everything else that happened - it is not an event the
 * simulation could log, because it happens before the simulation exists.
 */
export interface FeedNotice { id: number; t: number; source: string; text: string }

/** The four terrain layers an AO (area of operations) ships as raw binaries. */
const AO_LAYERS = ['cost', 'cover', 'elev', 'roads'] as const;

/** Every run of the demo cut uses one seed so the same quick start always fights the same battle. */
export const DEMO_SEED = 20260920;

/** Kept short so the feed stays fast; the debrief re-reads the full log from the worker at the end. */
const MAX_EVENTS = 4000;

interface State {
  stage: Stage;
  setStage: (s: Stage) => void;
  /** The last non-`'design'` stage; the design record's "Back" chip returns here. */
  previousStage: Stage;

  // ---- run state -------------------------------------------------------------------------------
  sim: SimClient | null;
  meta: AoMeta | null;
  /** The raw AO layers, kept so a future restart can re-init the worker without another fetch. */
  buffers: AoBuffers | null;
  /** Main-thread copy of the terrain grid: legality checks, cell math and grid references need it. */
  grid: Grid | null;
  scenario: Scenario | null;
  dials: Dials;
  snapshot: Snapshot | null;
  events: Event[];
  speed: Speed;
  /**
   * Set the moment the clock auto-slows to 10:1 for a significant event (pitch 2); carries the speed to
   * return to and the real time it returns there by itself. Cleared by any manual speed change,
   * including the resume chip the top bar shows while this is set.
   */
  autoSlow: AutoSlow | null;
  /** `Date.now()` of the last auto-slow, so the next significant event waits out the 30 s cooldown. */
  lastAutoSlowAt: number;
  ended: Snapshot['ended'];
  loading: boolean;
  error: string | null;

  // ---- planning stage ---------------------------------------------------------------------------
  /** The player's opening orders, issued as one play named "plan" when the fight starts. */
  plan: PlanOrder[];
  /** Units deliberately held back: no opening order, a RESERVE badge on the card. */
  reserve: number[];
  /** `Date.now()` in milliseconds when the planning clock hits zero; null before the clock starts. */
  planDeadline: number | null;
  /** Staff lines for the report feed that the simulation did not log. */
  notices: FeedNotice[];
  addNotice: (text: string, source?: string) => void;

  // ---- player interaction ----------------------------------------------------------------------
  selectedUnit: number | null;
  targeting: { card: CardId } | null;
  /** Why the last order attempt was refused; shown on the targeting banner and cleared on the next action. */
  targetingError: string | null;

  // ---- actions ---------------------------------------------------------------------------------
  quickStart: (aoName: string) => Promise<void>;
  chooseAo: (aoName: string) => Promise<void>;
  /**
   * Pitch 8, "shareable fights": loads a shared fight's area of operations and re-fits its scenario with
   * the record's own seed and dials (not `DEMO_SEED`), then reattaches the control graphics the sharer
   * drew. Leaves `stage` and `plan` alone - the landing card (`src/ui/stages/SharedFightLanding.tsx`)
   * decides what happens next: "fight it yourself" adds the recorded plan and starts the planning clock,
   * "watch the replay" only needs the AO in the store for the debrief to render against.
   */
  loadSharedScenario: (aoName: string, seed: number, dials: Dials, drawnGraphics: Graphic[]) => Promise<void>;
  beginPlanning: () => void;
  addGraphic: (g: Graphic) => void;
  renameGraphic: (id: string, name: string) => void;
  removeGraphic: (id: string) => void;
  addPlanOrder: (order: PlanOrder) => Legality;
  removePlanOrder: (unitId: number) => void;
  toggleReserve: (unitId: number) => void;
  executePlan: () => Promise<void>;
  issue: (unitId: number, card: CardId, target: Target) => void;
  cancel: (unitId: number) => void;
  setSpeed: (speed: Speed) => void;
  /** The auto-slow notice's chip: restores the speed the clock was at before the auto-slow fired. */
  resumeSpeed: () => void;
  /**
   * "The player is doing something." Called by every order issued and every unit selected (a speed
   * chip needs no call - it sets the speed, which retires the auto-slow outright). While an auto-slow
   * is counting down this holds it at 10:1 instead of letting it restore itself.
   */
  noteInteraction: () => void;
  setDials: (patch: Partial<Dials>) => void;
  select: (unitId: number | null) => void;
  startTargeting: (card: CardId) => void;
  stopTargeting: () => void;
}

async function fetchBuffers(aoName: string): Promise<AoBuffers> {
  const parts = await Promise.all(
    AO_LAYERS.map(async (layer) => {
      const res = await fetch(`/ao/${aoName}/${layer}.bin`);
      if (!res.ok) throw new Error(`${layer}.bin: ${res.status}`);
      return res.arrayBuffer();
    }),
  );
  return { cost: parts[0], cover: parts[1], elev: parts[2], roads: parts[3] };
}

/**
 * Fetches an AO and fits the scenario template onto it. Shared by the quick start, the scenario stage's
 * `chooseAo`, and (pitch 8, "shareable fights") a shared fight's landing card, which is the one caller
 * that ever passes a `seed` other than the demo's - every ordinary path through the app fights
 * `DEMO_SEED`, but a fight record carries whatever seed the sharer's run actually used (see
 * `src/share/fight.ts`), and re-fitting with anything else would build a different battle. Neither this
 * nor its callers spawn the worker here, so the planning stage can hold a fitted scenario with no
 * simulation running behind it.
 *
 * The worker later gets a structured-clone copy of these buffers (nothing is transferred), so the
 * main-thread Grid built here stays valid for legality checks and grid references.
 */
export async function loadAo(aoName: string, dials: Dials, seed: number = DEMO_SEED): Promise<{
  meta: AoMeta; buffers: AoBuffers; grid: Grid; scenario: Scenario;
}> {
  const metaRes = await fetch(`/ao/${aoName}/meta.json`);
  if (!metaRes.ok) throw new Error(`meta.json: ${metaRes.status}`);
  const meta = (await metaRes.json()) as AoMeta;
  const buffers = await fetchBuffers(aoName);
  const grid = Grid.fromBuffers(meta, buffers);
  return { meta, buffers, grid, scenario: fitScenario(grid, seed, dials) };
}

/**
 * The blue force as the legality checker sees it before the fight starts: one full-strength unit per
 * scenario placement. The simulation builds its units from the same list in the same order, so these
 * ids are the ids the worker will use — that is what lets a plan made here be issued verbatim at
 * H-hour (see `Sim`'s constructor).
 */
export function planUnits(scenario: Scenario): Unit[] {
  return scenario.blue.map((p, id) => ({
    id,
    side: 0 as const,
    type: p.type,
    callsign: p.callsign,
    x: p.x,
    y: p.y,
    strength: 100, suppression: 0, morale: 100, fuel: 100, ammo: 100,
    posture: 'hold' as const,
    alive: true, inContact: false, contactSince: -1,
    destCell: -1, orderId: -1, replanUntil: 0, brokenUntil: 0,
    uasAlive: false, lastReportTick: 0, movedCm: 0, pinned: false, firedThisMin: false,
    crossedPls: [],
  }));
}

export const useStore = create<State>((set, get) => ({
  stage: 'scenario',
  previousStage: 'scenario',
  // Records where the player was before opening `/design` so its "Back" chip can return them there;
  // a visit to `'design'` never overwrites the value already held.
  setStage: (stage) => set((s) => ({ stage, previousStage: s.stage === 'design' ? s.previousStage : s.stage })),

  sim: null,
  meta: null,
  buffers: null,
  grid: null,
  scenario: null,
  dials: DEFAULT_DIALS,
  snapshot: null,
  events: [],
  speed: 0,
  autoSlow: null,
  lastAutoSlowAt: 0,
  ended: null,
  loading: false,
  error: null,

  plan: [],
  reserve: [],
  planDeadline: null,
  notices: [],

  /** Negative ids, counting down, so a notice can never collide with an event id in the feed's map. */
  addNotice(text, source = 'S-3') {
    const notices = get().notices;
    set({ notices: [...notices, { id: -1_000_000 - notices.length, t: 0, source, text }] });
  },

  selectedUnit: null,
  targeting: null,
  targetingError: null,

  /**
   * Spins up the simulation worker on an already-fitted scenario and jumps straight to the live stage
   * at 1x, skipping the OPORD (operation order) and the planning clock. Kept as a small secondary link
   * on the scenario stage, and as the fast path the end-to-end test takes.
   */
  async quickStart(aoName: string) {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { meta, buffers, grid, scenario } = await loadAo(aoName, get().dials);
      set({ meta, buffers, grid, scenario, plan: [], reserve: [], notices: [] });
      await startSim(set, get);
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  /**
   * Stage 1: loads the AO, fits the scenario onto it and stops. No worker is spawned — the OPORD and
   * the planning stage read the fitted scenario, and the simulation only starts at `executePlan`.
   */
  async chooseAo(aoName: string) {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { meta, buffers, grid, scenario } = await loadAo(aoName, get().dials);
      get().sim?.dispose();
      set({
        sim: null, meta, buffers, grid, scenario,
        snapshot: null, events: [], ended: null, speed: 0,
        plan: [], reserve: [], planDeadline: null, notices: [],
        selectedUnit: null, targeting: null, targetingError: null,
        stage: 'planning', loading: false,
      });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  async loadSharedScenario(aoName, seed, dials, drawnGraphics) {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { meta, buffers, grid, scenario } = await loadAo(aoName, dials, seed);
      get().sim?.dispose();
      set({
        sim: null, meta, buffers, grid,
        scenario: { ...scenario, graphics: [...scenario.graphics, ...drawnGraphics] },
        dials,
        snapshot: null, events: [], ended: null, speed: 0,
        selectedUnit: null, targeting: null, targetingError: null,
        loading: false,
      });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  /** "Begin planning": starts the planning clock. Stage 2 runs until it reaches zero or the player executes. */
  beginPlanning() {
    set({ stage: 'planning', planDeadline: Date.now() + get().dials.planMin * 60_000 });
  },

  // ---- control graphics the player draws ---------------------------------------------------------
  // `scenario` is replaced rather than mutated so the overlay's store subscription sees the change and
  // redraws, and so the worker is handed the extended graphics list at init.

  addGraphic(g) {
    const scenario = get().scenario;
    if (!scenario) return;
    set({ scenario: { ...scenario, graphics: [...scenario.graphics, g] } });
  },

  renameGraphic(id, name) {
    const scenario = get().scenario;
    if (!scenario) return;
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    set({
      scenario: {
        ...scenario,
        graphics: scenario.graphics.map((g) => (g.id === id ? { ...g, name: trimmed } : g)),
      },
    });
  },

  removeGraphic(id) {
    const scenario = get().scenario;
    if (!scenario) return;
    set({
      scenario: { ...scenario, graphics: scenario.graphics.filter((g) => g.id !== id) },
      // A plan order aimed at a graphic that no longer exists would be refused at H-hour; drop it here.
      plan: get().plan.filter((o) => o.target.graphicId !== id),
    });
  },

  // ---- the plan -----------------------------------------------------------------------------------

  /**
   * Adds (or replaces) one unit's opening order. The same legality check the sim runs decides, so a
   * refused task can say why on the card instead of failing silently at H-hour.
   */
  addPlanOrder(order) {
    const { scenario, grid } = get();
    if (!scenario || !grid) return { ok: false, reason: 'No scenario loaded' };
    const units = planUnits(scenario);
    const unit = units.find((u) => u.id === order.unitId);
    if (!unit) return { ok: false, reason: 'That unit is not in the task organization' };
    const legal = isLegal(order.card, unit, order.target, grid, scenario.graphics, units);
    if (!legal.ok) return legal;
    set({
      plan: [...get().plan.filter((o) => o.unitId !== order.unitId), order],
      reserve: get().reserve.filter((id) => id !== order.unitId),
    });
    return { ok: true };
  },

  removePlanOrder(unitId) {
    set({ plan: get().plan.filter((o) => o.unitId !== unitId) });
  },

  /** A unit in reserve gets no opening order at all; it waits in the assembly area for a FRAGO. */
  toggleReserve(unitId) {
    const reserve = get().reserve;
    if (reserve.includes(unitId)) {
      set({ reserve: reserve.filter((id) => id !== unitId) });
      return;
    }
    set({ reserve: [...reserve, unitId], plan: get().plan.filter((o) => o.unitId !== unitId) });
  },

  /**
   * H-hour: spawns the worker on the scenario as planned (fitted graphics plus everything the player
   * drew), issues the whole plan as one play so the debrief can trace every opening order back to it,
   * and hands over to the live stage at 1x. The planning clock calls this too when it reaches zero.
   */
  async executePlan() {
    if (get().loading || get().sim) return;
    set({ loading: true, error: null });
    try {
      await startSim(set, get);
      const { sim, plan } = get();
      if (sim && plan.length > 0) sim.issuePlay('plan', plan);
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  /**
   * Issues one order card. The legality check runs here as well as in the sim so a refused order can
   * say why immediately instead of only appearing as an `order.rejected` event a tick later.
   */
  issue(unitId, cardId, target) {
    const { sim, snapshot, grid, scenario } = get();
    if (!sim || !snapshot || !grid || !scenario) return;
    // Any attempt at an order counts, legal or not: the player is at the controls either way.
    get().noteInteraction();
    const unit = snapshot.units.find((u) => u.id === unitId);
    if (!unit) {
      set({ targetingError: 'No unit selected' });
      return;
    }
    if (cardId === 'cancel') {
      sim.cancel(unitId);
      set({ targeting: null, targetingError: null });
      return;
    }
    const legal = isLegal(cardId, unit, target, grid, scenario.graphics, snapshot.units);
    if (!legal.ok) {
      set({ targetingError: legal.reason });
      return;
    }
    sim.issue(unitId, cardId, target);
    set({ targeting: null, targetingError: null });
  },

  cancel(unitId) {
    get().noteInteraction();
    get().sim?.cancel(unitId);
    set({ targeting: null, targetingError: null });
  },

  setSpeed(speed) {
    get().sim?.setSpeed(speed);
    // Any manual speed change - including clicking the very speed the auto-slow dropped to - retires the
    // notice; it is not the player's job to dismiss it separately from acting on it.
    set({ speed, autoSlow: null });
  },

  resumeSpeed() {
    const { autoSlow } = get();
    if (!autoSlow) return;
    get().setSpeed(autoSlow.previousSpeed);
  },

  noteInteraction() {
    const { autoSlow } = get();
    const held = autoSlowHold(autoSlow);
    if (held !== autoSlow) set({ autoSlow: held });
  },

  setDials(patch) {
    const dials = { ...get().dials, ...patch };
    const { grid, sim, scenario } = get();
    set({ dials });
    // The enemy's posture decides where the fitter puts red, so turning that dial before the fight
    // re-fits the scenario. Only ever before the worker exists: once a fight is running the dials that
    // matter (command-and-control delay, electronic warfare, time of day) are read live by the sim.
    if (patch.red !== undefined && sim === null && grid && scenario) {
      set({ scenario: fitScenario(grid, DEMO_SEED, dials), plan: [], reserve: [] });
    }
  },

  select(unitId) {
    // Picking a unit is the player working the problem: it holds an auto-slow that was about to put
    // the clock back. Clearing the selection (a click on bare ground) is not.
    if (unitId !== null) get().noteInteraction();
    // Selecting a different unit abandons a target the previous unit was waiting on.
    set({ selectedUnit: unitId, targeting: null, targetingError: null });
  },

  startTargeting(card) {
    set({ targeting: { card }, targetingError: null });
  },

  stopTargeting() {
    set({ targeting: null, targetingError: null });
  },
}));

type SetState = (partial: Partial<State>) => void;
type GetState = () => State;

/**
 * Spawns the simulation worker on the currently loaded AO and scenario and moves to the live stage at
 * 1x. Handlers are registered before `init` so the tick-0 snapshot (everyone in the assembly area) is
 * not missed while the clock is still paused.
 */
async function startSim(set: SetState, get: GetState): Promise<void> {
  const { meta, buffers, scenario, dials } = get();
  if (!meta || !buffers || !scenario) throw new Error('No area of operations loaded');

  get().sim?.dispose();
  const sim = new SimClient();
  sim.onSnapshot((snap, newEvents) => {
    if (newEvents.length === 0) {
      set({ snapshot: snap });
    } else {
      const events = get().events.concat(newEvents);
      set({
        snapshot: snap,
        events: events.length > MAX_EVENTS ? events.slice(events.length - MAX_EVENTS) : events,
      });
      maybeAutoSlow(set, get, newEvents);
    }
    // The restore rides the snapshot stream (roughly ten a second) rather than a timer of its own:
    // there is no second clock to leak, and the speed only ever goes back up while the simulation is
    // actually running.
    maybeRestoreAutoSlow(set, get);
  });
  sim.onEnded((snap) => set({ snapshot: snap, ended: snap.ended, speed: 0, autoSlow: null }));

  await sim.init(meta, buffers, scenario, dials, DEMO_SEED);

  set({
    sim,
    events: [], ended: null, selectedUnit: null, targeting: null, targetingError: null,
    planDeadline: null,
    stage: 'live', loading: false,
  });
  get().setSpeed(SPEED_60_1);
}

/**
 * The event kinds worth breaking pace for: new contacts, an NAI (named area of interest) tripped, a unit
 * broken/destroyed/lost, the force running low on fuel, culmination, a saturated net, and a disclosed red
 * decision. Deliberately excludes routine mechanics of the player's own plan playing out on schedule -
 * `pl.crossed` (a friendly platoon crossing its own phase line) and every `order.*` lifecycle kind - which
 * fire constantly at 240:1 and would otherwise collapse the clock back to 10:1 every few seconds.
 */
export const AUTO_SLOW_KINDS: ReadonlySet<EventKind> = new Set<EventKind>([
  'contact.new', 'nai.contact', 'unit.broken', 'unit.destroyed', 'force.fuelLow', 'culminated',
  'net.saturated', 'uas.lost', 'obj.seized', 'red.decision',
]);

/** The slice of the store `autoSlowTrigger` reads - named so the rule can be unit-tested without one. */
export interface AutoSlowInput {
  dials: Dials;
  speed: Speed;
  autoSlow: AutoSlow | null;
  lastAutoSlowAt: number;
}

/**
 * Pitch 2, "time you can feel": a significant side-0 event at faster than 10:1 drops the clock to 10:1
 * so the player actually sees the event land, unless Live Pressure (`dials.pressure`) is on - that dial
 * removes pause for the same reason it should remove this. Fires at most once per 30 real seconds so a
 * burst of significant events (a phase line crossed by six platoons in the same tick) does not fight the
 * player for the speed chip. Restricted to `AUTO_SLOW_KINDS` so routine order and phase-line events - which
 * are marked `significant` for the report feed's own reasons - never trigger it.
 *
 * Pure: returns the auto-slow to start, or null to leave the clock alone.
 */
export function autoSlowTrigger(s: AutoSlowInput, newEvents: Event[], now: number): AutoSlow | null {
  if (s.dials.pressure || s.speed <= SPEED_10_1 || s.autoSlow) return null;
  if (now - s.lastAutoSlowAt < AUTO_SLOW_COOLDOWN_MS) return null;
  const significant = newEvents.some((e) => e.side === 0 && e.significant && AUTO_SLOW_KINDS.has(e.kind));
  if (!significant) return null;
  return { previousSpeed: s.speed, restoreAt: now + AUTO_SLOW_RESTORE_MS, held: false };
}

/** Pure: is the clock due to put itself back? Never while the auto-slow is held. */
export function autoSlowRestoreDue(autoSlow: AutoSlow | null, now: number): boolean {
  return autoSlow !== null && !autoSlow.held && now >= autoSlow.restoreAt;
}

/**
 * Pure: whole real seconds left before the clock restores itself, or null when there is no countdown
 * to show - no auto-slow, or one the player's own actions have put on hold.
 */
export function autoSlowSecondsLeft(autoSlow: AutoSlow | null, now: number): number | null {
  if (autoSlow === null || autoSlow.held) return null;
  return Math.max(0, Math.ceil((autoSlow.restoreAt - now) / 1000));
}

/** Pure: the auto-slow after the player has touched something. Identity when there is nothing to hold. */
export function autoSlowHold(autoSlow: AutoSlow | null): AutoSlow | null {
  if (autoSlow === null || autoSlow.held) return autoSlow;
  return { ...autoSlow, held: true };
}

function maybeAutoSlow(set: SetState, get: GetState, newEvents: Event[]): void {
  const s = get();
  if (!s.sim) return;
  const now = Date.now();
  const next = autoSlowTrigger(s, newEvents, now);
  if (!next) return;
  s.sim.setSpeed(SPEED_10_1);
  set({ speed: SPEED_10_1, autoSlow: next, lastAutoSlowAt: now });
}

/** The other half of the rule: twenty untouched seconds and the clock goes back where it was. */
function maybeRestoreAutoSlow(set: SetState, get: GetState): void {
  const { autoSlow, sim } = get();
  if (!sim || !autoSlowRestoreDue(autoSlow, Date.now())) return;
  // `setSpeed` is the manual path too, and it clears `autoSlow` for us.
  get().setSpeed(autoSlow!.previousSpeed);
}

// Preserve store state across Vite hot reloads in development. Without this, every save of a module that
// imports the store re-creates it and the app snaps back to the scenario stage.
if (import.meta.hot) {
  const saved = import.meta.hot.data.state as Partial<ReturnType<typeof useStore.getState>> | undefined;
  if (saved) useStore.setState(saved as never);
  import.meta.hot.dispose((data) => { data.state = useStore.getState(); });
  import.meta.hot.accept();
}
